import CustomFields from 'components/forms/inputs/custom-fields/CustomFields';
import { Row } from 'components/GridItemList';
import Loader from 'components/Loader';
import { SectionEdit, SectionEditForm } from 'components/section-edit/SectionEdit';
import { useFrozenOnceLoaded } from 'components/section-edit/useFrozenOnceLoaded';
import { saveSection } from 'components/section-edit/saveSection';
import { ReactNode } from 'react';
import { Control, FieldValues } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useGetJobPositionByIdQuery, useUpdateJobJobPositionMutation } from 'store/api/jobPositions';
import { ClassifierData } from 'types/classifiers';
import { CustomField } from 'types/customFields';
import { JobPositionDto, JobPositionForm } from 'types/jobPositions';
import { useClassifiers } from 'utils/useClassifiers';
import {
    JOB_POSITION_SECTION_FIELDS,
    JobPositionSectionId,
    jobPositionDtoToForm,
    jobPositionFormToRequest,
} from '../jobPositionFormMapping';
import { JobPositionFields, JobPositionTranslationsFields } from './JobPositionFields';
import { JOB_POSITION_FORM_CLASSIFIERS, JobPositionFormState, useJobPositionForm } from './useJobPositionForm';

const SECTION_FIELDS: Record<JobPositionSectionId, (form: JobPositionFormState) => ReactNode> = {
    'job-position': (form) => <JobPositionFields form={form} isExternalIdReadOnly />,
    translations: (form) => <JobPositionTranslationsFields form={form} />,
    'custom-fields': (form) => (
        <CustomFields control={form.methods.control as unknown as Control<FieldValues>} fields={form.customFields} />
    ),
};

interface SectionFormProps {
    section: JobPositionSectionId;
    initialData: JobPositionForm;
    customFields: CustomField[] | undefined;
    classifiers: Record<string, ClassifierData>;
    onClose: () => void;
}

function JobPositionSectionForm({ section, initialData, customFields, classifiers, onClose }: SectionFormProps) {
    // The page's own query argument, so the refetch refreshes the job position the page shows.
    const { id: jobPositionId = '' } = useParams();
    const { refetch } = useGetJobPositionByIdQuery(jobPositionId);
    const [updateJobPosition] = useUpdateJobJobPositionMutation();
    const form = useJobPositionForm({ initialData, classifiers, entityCustomFields: customFields });

    const onSave = (values: JobPositionForm) =>
        saveSection(values, {
            section,
            sectionFields: JOB_POSITION_SECTION_FIELDS,
            fetchLatest: () => refetch().unwrap(),
            toForm: (jobPosition) => ({
                ...jobPositionDtoToForm(jobPosition, classifiers),
                ...form.customFieldValuesOf(jobPosition.customFields),
            }),
            toRequest: (edited) =>
                jobPositionFormToRequest(
                    edited,
                    form.mapCustomFieldsToDto(edited as unknown as Record<string, unknown>)
                ),
            update: (request) => updateJobPosition({ ...request, id: Number(jobPositionId) }).unwrap(),
            // The update endpoint has no toast of its own.
            successMessageId: 'job-positions.job-position-updated',
            onSaved: onClose,
        });

    if (form.isLoadingCustomFieldData) {
        return <Loader />;
    }
    return (
        <SectionEditForm methods={form.methods} onSave={onSave} onCancel={onClose}>
            {SECTION_FIELDS[section](form)}
        </SectionEditForm>
    );
}

interface EditorProps {
    section: JobPositionSectionId;
    jobPosition: JobPositionDto;
    onClose: () => void;
}

function JobPositionSectionEditor({ section, jobPosition, onClose }: EditorProps) {
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: JOB_POSITION_FORM_CLASSIFIERS,
    });

    // Custom fields frozen too: the form takes their values from them.
    const frozen = useFrozenOnceLoaded(!isLoadingClassifiers, () => ({
        initialData: jobPositionDtoToForm(jobPosition, classifiers),
        customFields: jobPosition.customFields,
    }));

    if (!frozen) {
        return <Loader />;
    }
    return (
        <JobPositionSectionForm
            section={section}
            initialData={frozen.initialData}
            customFields={frozen.customFields}
            classifiers={classifiers}
            onClose={onClose}
        />
    );
}

interface JobPositionSectionProps {
    id: JobPositionSectionId;
    titleId: string;
    jobPosition: JobPositionDto;
    /** The read view, handed the card's Edit control. */
    children: (edit: ReactNode) => ReactNode;
    /** The read view's rows: those no field edits stay under the fields while the card edits. */
    rows?: Row[];
}

/** A card of the job position page that edits in place with the job position form's fields for that card. */
export function JobPositionSection({ id, titleId, jobPosition, children, rows }: JobPositionSectionProps) {
    return (
        <SectionEdit
            id={id}
            titleId={titleId}
            editor={(close) => <JobPositionSectionEditor section={id} jobPosition={jobPosition} onClose={close} />}
            rows={rows}
        >
            {children}
        </SectionEdit>
    );
}
